import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ListingCard } from "./ListingCard";
import { Link } from "react-router-dom";

type Props = {
  listings: any[];
  title?: string;
};

function pickFirstPhoto(...candidates: Array<string | null | undefined>) {
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (s) return s;
  }
  return null;
}

function normalizeImages(item: any): string[] {
  const raw = Array.isArray(item?.imagens)
    ? item.imagens
    : Array.isArray(item?.images)
      ? item.images
      : [];

  const cleaned = raw
    .map((img: any) => String(img ?? "").trim())
    .filter(Boolean);

  return [...new Set(cleaned)] as string[];
}

export function RecommendationCarousel({
  listings,
  title = "Recomendados pra você",
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const uniqueListings = useMemo(() => {
    const seen = new Set<string>();
    const out: any[] = [];

    for (const item of listings ?? []) {
      if (item?.type !== "servico") {
        out.push(item);
        continue;
      }

      const hostId = String(item?.anfitriao?.id ?? "");
      const key =
        hostId ||
        `${item?.anfitriao?.nome ?? ""}::${item?.anfitriao?.cidade ?? ""}::${item?.servico_id ?? item?.id ?? ""}`;

      if (seen.has(key)) continue;

      seen.add(key);
      out.push(item);
    }

    return out;
  }, [listings]);

  const updateButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < maxLeft - 8);
  };

  useEffect(() => {
    updateButtons();

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);

    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, [uniqueListings]);

  useEffect(() => {
    if (paused) return;

    const id = window.setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;

      const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const nearEnd = el.scrollLeft >= maxLeft - 12;

      if (nearEnd) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: 340, behavior: "smooth" });
      }
    }, 4000);

    return () => window.clearInterval(id);
  }, [paused]);

  const scroll = (direction: "left" | "right") => {
    scrollRef.current?.scrollBy({
      left: direction === "left" ? -340 : 340,
      behavior: "smooth",
    });
  };

  return (
    <section className="py-6 md:py-8">
      <div className="container px-4 md:px-6">
        <div className="mb-4 flex items-center justify-between gap-3 md:mb-5">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold tracking-tight md:text-2xl">
              {title}
            </h2>

            <Link
              to="/buscar"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-muted/80"
              aria-label="Ver todos"
              title="Ver todos"
            >
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="hidden items-center gap-2 md:flex">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full border bg-background shadow-sm disabled:opacity-40"
              onClick={() => scroll("left")}
              disabled={!canLeft}
              aria-label="Voltar"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full border bg-background shadow-sm disabled:opacity-40"
              onClick={() => scroll("right")}
              disabled={!canRight}
              aria-label="Avançar"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="
            flex gap-4 overflow-x-auto pb-2
            snap-x snap-mandatory
            scrollbar-hide
            scroll-smooth
          "
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          {uniqueListings.map((item: any) => {
            if (item?.type === "banner" || item?.type === "cta") {
              const isCta = item.type === "cta";

              return (
                <div
                  key={String(item.id)}
                  className="
                    min-w-[280px] max-w-[280px] shrink-0 snap-start
                    rounded-2xl border p-6
                    flex flex-col justify-between
                    shadow-sm
                    md:min-w-[320px] md:max-w-[320px]
                    bg-card
                  "
                >
                  <div>
                    <div className="mb-2 text-lg font-bold">{item.titulo}</div>
                    <div className="text-sm text-muted-foreground">
                      {item.subtitulo}
                    </div>
                  </div>

                  <Button
                    asChild
                    className="mt-6 w-full rounded-full"
                    variant={isCta ? "default" : "outline"}
                  >
                    <a href={item.cta?.href}>{item.cta?.label}</a>
                  </Button>
                </div>
              );
            }

            const isServico =
              item?.type === "servico" ||
              item?.servico_id != null ||
              item?.tipo_servico_id != null;

            if (isServico) {
              const imagensServico = normalizeImages(item);

              const fotoFallback = pickFirstPhoto(
                item?.anfitriao?.foto,
                item?.anfitriao?.foto_perfil,
                item?.host_profile?.foto_perfil,
                item?.foto_perfil
              );

              const imagensFinais =
                imagensServico.length > 0
                  ? imagensServico
                  : fotoFallback
                    ? [fotoFallback]
                    : [];

              const fixedItem = {
                ...item,
                type: "servico",
                anfitriao: {
                  ...item?.anfitriao,
                  foto: fotoFallback ?? item?.anfitriao?.foto,
                },
                imagens: imagensFinais,
              };

              return (
                <div
                  key={String(item.servico_id ?? item.id)}
                  className="
                    min-w-[280px] max-w-[280px] shrink-0 snap-start
                    md:min-w-[300px] md:max-w-[300px]
                    lg:min-w-[310px] lg:max-w-[310px]
                  "
                >
                  <ListingCard listing={fixedItem as any} />
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    </section>
  );
}